'use client';

import { LocalChannel, resetRealtimeConnection } from './realtime';
import { clearInteractionCache } from './interaction-cache';

type Result = { data: any; error: Error | null; count?: number | null };

class LocalQuery implements PromiseLike<Result> {
  private table: string;
  private action = 'select';
  private filters: Array<{ op: string; column: string; value: unknown }> = [];
  private payload: unknown;
  private options: Record<string, unknown> = {};

  constructor(table: string) { this.table = table; }
  select(columns = '*', options?: unknown) { this.options.columns = columns; this.options.selectOptions = options; return this; }
  insert(value: unknown) { this.action = 'insert'; this.payload = value; return this; }
  update(value: unknown) { this.action = 'update'; this.payload = value; return this; }
  upsert(value: unknown, options?: unknown) { this.action = 'upsert'; this.payload = value; this.options.upsert = options; return this; }
  delete() { this.action = 'delete'; return this; }
  eq(column: string, value: unknown) { this.filters.push({ op: 'eq', column, value }); return this; }
  neq(column: string, value: unknown) { this.filters.push({ op: 'neq', column, value }); return this; }
  is(column: string, value: unknown) { this.filters.push({ op: 'is', column, value }); return this; }
  in(column: string, value: unknown[]) { this.filters.push({ op: 'in', column, value }); return this; }
  ilike(column: string, value: unknown) { this.filters.push({ op: 'ilike', column, value }); return this; }
  like(column: string, value: unknown) { this.filters.push({ op: 'like', column, value }); return this; }
  gt(column: string, value: unknown) { this.filters.push({ op: 'gt', column, value }); return this; }
  gte(column: string, value: unknown) { this.filters.push({ op: 'gte', column, value }); return this; }
  lt(column: string, value: unknown) { this.filters.push({ op: 'lt', column, value }); return this; }
  lte(column: string, value: unknown) { this.filters.push({ op: 'lte', column, value }); return this; }
  contains(column: string, value: unknown) { this.filters.push({ op: 'contains', column, value }); return this; }
  or(value: string) { this.options.or = value; return this; }
  order(column: string, value?: unknown) { this.options.order = { column, value }; return this; }
  limit(value: number) { this.options.limit = value; return this; }
  range(from: number, to: number) { this.options.range = [from, to]; return this; }
  single() { this.options.single = true; return this; }
  maybeSingle() { this.options.maybeSingle = true; return this; }

  async execute(): Promise<Result> {
    try {
      const response = await fetch('/api/local-db', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ table: this.table, action: this.action, filters: this.filters, payload: this.payload, options: this.options }),
      });
      const result = await response.json();
      if (response.ok && this.action !== 'select') clearInteractionCache();
      return response.ok ? { data: result.data, error: null, count: result.count } : { data: null, error: new Error(result.error) };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error : new Error('Local database request failed') };
    }
  }

  then<TResult1 = Result, TResult2 = never>(onfulfilled?: ((value: Result) => TResult1 | PromiseLike<TResult1>) | null, onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null): PromiseLike<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }
}

const emptySubscription = { unsubscribe() {} };
const authListeners = new Set<(event: string, session: any) => void>();

async function authRequest(path: string, body?: unknown) {
  const response = await fetch(path, {
    method: body === undefined ? 'GET' : 'POST',
    headers: body === undefined ? undefined : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok) return { data: null, error: new Error(result.error ?? 'Authentication failed') };
  return { data: result.data, error: null };
}

let client: any;
export function createClient(): any {
  if (client) return client;
  client = {
    from: (table: string) => new LocalQuery(table),
    auth: {
      getSession: async () => authRequest('/api/auth/session'),
      getUser: async () => {
        const result = await authRequest('/api/auth/session');
        return { data: { user: result.data?.user ?? null }, error: result.error };
      },
      signUp: async ({ identifier, email, phone, password, options }: any) => {
        const result = await authRequest('/api/auth/signup', { identifier: identifier ?? email ?? phone, password, metadata: options?.data ?? {} });
        if (!result.error) { resetRealtimeConnection(); authListeners.forEach((listener) => listener('SIGNED_IN', result.data?.session)); }
        return result;
      },
      signInWithPassword: async ({ identifier, email, phone, password }: any) => {
        const result = await authRequest('/api/auth/login', { identifier: identifier ?? email ?? phone, password });
        if (!result.error) { resetRealtimeConnection(); authListeners.forEach((listener) => listener('SIGNED_IN', result.data?.session)); }
        return result;
      },
      signOut: async () => {
        const result = await authRequest('/api/auth/logout', {});
        if (!result.error) { resetRealtimeConnection(); authListeners.forEach((listener) => listener('SIGNED_OUT', null)); }
        return { error: result.error };
      },
      onAuthStateChange: (listener: (event: string, session: any) => void) => {
        authListeners.add(listener);
        return { data: { subscription: { ...emptySubscription, unsubscribe: () => authListeners.delete(listener) } } };
      },
    },
    channel: () => new LocalChannel(),
    removeChannel: (channel: LocalChannel) => { channel.unsubscribe(); return Promise.resolve('ok'); },
    rpc: () => Promise.resolve({ data: null, error: null }),
    storage: { from: (bucket: string) => ({
      upload: async (path: string, file: File, options?: { upsert?: boolean }) => {
        try {
          const form = new FormData();
          form.set('bucket', bucket);
          form.set('path', path);
          form.set('upsert', String(options?.upsert ?? false));
          form.set('file', file);
          const response = await fetch('/api/uploads', { method: 'POST', body: form });
          const result = await response.json();
          return response.ok
            ? { data: { path: result.data.path }, error: null }
            : { data: null, error: new Error(result.error ?? 'Upload failed') };
        } catch (error) {
          return { data: null, error: error instanceof Error ? error : new Error('Upload failed') };
        }
      },
      getPublicUrl: (path: string) => ({
        data: { publicUrl: `/api/uploads?bucket=${encodeURIComponent(bucket)}&path=${encodeURIComponent(path)}` },
      }),
    }) },
  };
  return client;
}
